<template>
    <div class="container_view">
        <v-card class="content_card" elevation="10" variant="flat">
            <div>
                <header>
                    <v-card-title> Configuración de Campos del Deudor </v-card-title>
                    <v-card-subtitle> La configuración de los campos se administra de forma <strong>independiente</strong> para cada <strong>pestaña</strong>. </v-card-subtitle>
                    <v-divider class="mx-3"></v-divider>
                </header>
            </div>

            <div class="content_main" v-if="isAllowed && !isLoading">
                <div class="toolbar">
                    <v-select
                        label="Perfil / Tenant"
                        v-model="selectedProfileId"
                        :items="profiles"
                        item-title="label"
                        item-value="id"
                        variant="outlined"
                        class="profile_select"
                        prepend-icon="mdi-account-cog"
                        :disabled="isSaving"
                        @update:model-value="loadProfileFields"
                    ></v-select>
                </div>

                <div class="tabs_bar">
                    <v-chip
                        v-for="tab in tabList"
                        :key="tab.key"
                        class="tab_chip"
                        :color="selectedTab === tab.key ? 'indigo-darken-3' : ''"
                        :text-color="selectedTab === tab.key ? 'white' : ''"
                        @click="selectedTab = tab.key"
                    >{{ tab.title }}</v-chip>
                </div>

                <div class="select_all_bar">
                    <v-checkbox
                        :model-value="allVisible"
                        :indeterminate="someVisible"
                        label="Mostrar todos los campos de esta pestaña"
                        color="indigo-darken-3"
                        density="compact"
                        hide-details
                        :disabled="isSaving || fields.length === 0"
                        @update:model-value="toggleAllVisible"
                    ></v-checkbox>
                    <span class="select_all_hint">{{ visibleCount }} de {{ fields.length }} campos visibles</span>
                </div>

                <div class="columns_header">
                    <div class="col_position">#</div>
                    <div class="col_key">Campo (clave fuente)</div>
                    <div class="col_name">Etiqueta personalizada</div>
                    <div class="col_visible">Mostrar</div>
                    <div class="col_actions">Acciones</div>
                </div>

                <div class="columns_list">
                    <div class="content_load" v-if="isLoadingColumns">
                        <v-progress-circular
                            :size="60"
                            :width="6"
                            color="indigo-darken-3"
                            indeterminate
                        ></v-progress-circular>
                        <i>Cargando campos...</i>
                    </div>

                    <div v-else-if="fields.length === 0" class="empty_state">
                        Esta pestaña no tiene campos configurados. Agrega uno desde el catálogo.
                    </div>

                    <div
                        v-for="(field, index) in fields"
                        :key="field.field_key"
                        class="column_row"
                    >
                        <div class="col_position">{{ index + 1 }}</div>
                        <div class="col_key">{{ field.field_key }}</div>
                        <div class="col_name">
                            <v-text-field
                                v-model="field.display_name"
                                label="Etiqueta"
                                density="compact"
                                variant="outlined"
                                hide-details
                                :disabled="isSaving"
                                :placeholder="field.field_key"
                            ></v-text-field>
                        </div>
                        <div class="col_visible">
                            <v-checkbox
                                v-model="field.is_visible"
                                color="indigo-darken-3"
                                density="compact"
                                hide-details
                                :disabled="isSaving"
                            ></v-checkbox>
                        </div>
                        <div class="col_actions">
                            <v-btn
                                icon
                                size="small"
                                variant="text"
                                :disabled="isSaving || index === 0"
                                @click="moveField(index, index - 1)"
                            >
                                <v-icon>mdi-arrow-up</v-icon>
                            </v-btn>
                            <v-btn
                                icon
                                size="small"
                                variant="text"
                                :disabled="isSaving || index === fields.length - 1"
                                @click="moveField(index, index + 1)"
                            >
                                <v-icon>mdi-arrow-down</v-icon>
                            </v-btn>
                            <v-btn
                                icon
                                size="small"
                                variant="text"
                                color="red"
                                :disabled="isSaving"
                                @click="removeField(field.field_key)"
                            >
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </div>
                    </div>
                </div>

                <div class="add_field">
                    <v-select
                        label="Agregar campo"
                        v-model="newFieldKey"
                        :items="availableCatalog"
                        variant="outlined"
                        class="catalog_select"
                        prepend-icon="mdi-plus"
                        :disabled="isSaving || availableCatalog.length === 0"
                    ></v-select>
                    <v-btn
                        variant="flat"
                        color="indigo-darken-3"
                        class="text-none elevation-0"
                        prepend-icon="mdi-plus-box"
                        :disabled="isSaving || !newFieldKey"
                        @click="addField"
                    >Agregar</v-btn>
                </div>

                <div class="actions_buttons">
                    <v-btn
                        variant="flat"
                        class="text-none elevation-0"
                        color="red"
                        :disabled="isSaving"
                        @click="discardChanges"
                        text="Descartar cambios"
                    ></v-btn>
                    <v-btn
                        variant="flat"
                        class="text-none elevation-0"
                        color="indigo-darken-3"
                        prepend-icon="mdi-content-save"
                        :disabled="isSaving || selectedProfileId === null"
                        @click="save"
                        text="Guardar configuración"
                    ></v-btn>
                </div>
            </div>

            <div class="content_load" v-if="isAllowed && isLoading">
                <v-progress-circular
                    :size="100"
                    :width="7"
                    color="indigo-darken-3"
                    indeterminate
                ></v-progress-circular>
                <i>Espere un momento</i>
                <div>Cargando configuración</div>
            </div>

            <div class="content_load" v-if="!isAllowed && !isLoading">
                <div>No tiene permisos para acceder a esta sección.</div>
            </div>
        </v-card>

        <SnackbarComponent :value="snackbarData"/>
    </div>
</template>

<script>
    import SnackbarComponent from '@/components/Snackbar'
    import { EncryptService } from '@/services/EncryptService'
    import { DebtorFieldService } from '@/services/DebtorFieldService'
    import { useDebtorFieldsStore } from '@/stores/debtorFields'

    const TAB_LIST = [
        { key: 'general', title: 'General' },
        { key: 'credito', title: 'Crédito' },
        { key: 'domicilios', title: 'Domicilios' },
        { key: 'trabajos', title: 'Trabajos' },
        { key: 'avales', title: 'Aval(es)' },
    ]

    export default {
        name: 'DebtorFieldConfigView',
        components: {
            SnackbarComponent,
        },
        data() {
            return {
                tabList: TAB_LIST,
                isAllowed: false,
                isLoading: false,
                isLoadingColumns: false,
                isSaving: false,
                profiles: [],
                catalog: [],
                tabs: {},
                fields: [],
                selectedProfileId: null,
                selectedTab: 'general',
                newFieldKey: null,
                snackbarData: {
                    openSnackbar: false,
                    description: '',
                },
            }
        },
        computed: {
            availableCatalog() {
                const used = new Set(this.fields.map((f) => f.field_key))
                return this.catalog.filter((key) => !used.has(key))
            },
            visibleCount() {
                return this.fields.filter((f) => f.is_visible).length
            },
            allVisible() {
                return this.fields.length > 0 && this.fields.every((f) => f.is_visible)
            },
            someVisible() {
                return this.fields.some((f) => f.is_visible)
            },
        },
        async created() {
            await this.checkAccess()
            if (!this.isAllowed) {
                return
            }

            this.isLoading = true
            await this.loadProfiles()
            this.isLoading = false
        },
        methods: {
            checkAccess: async function () {
                const ENC_TYPE = localStorage.getItem('TIPO')
                const TYPE_USER = await EncryptService.decrypt(ENC_TYPE)
                this.isAllowed = TYPE_USER === 'Superadministrador'
            },
            loadProfiles: async function () {
                await DebtorFieldService.getProfiles().then((responseData) => {
                    if (!responseData.error) {
                        this.profiles = (responseData.response.profiles || []).map((p) => ({
                            id: p.id,
                            label: `${p.tenant_code}${p.is_default ? ' (Por defecto)' : ''}`,
                            ...p,
                        }))
                        this.catalog = responseData.response.catalog || []

                        const defaultProfile = this.profiles.find((p) => p.is_default) || this.profiles[0]
                        if (defaultProfile) {
                            this.selectedProfileId = defaultProfile.id
                            this.loadProfileFields()
                        }
                    } else {
                        this.showSnackbar(responseData.msg)
                    }
                })
            },
            loadProfileFields: async function () {
                if (this.selectedProfileId === null || this.selectedProfileId === undefined) {
                    return
                }
                this.isLoadingColumns = true
                await DebtorFieldService.getProfileFields(this.selectedProfileId).then((responseData) => {
                    if (!responseData.error) {
                        this.tabs = responseData.response.tabs || {}
                        this.loadTabFields()
                    } else {
                        this.showSnackbar(responseData.msg)
                    }
                })
                this.isLoadingColumns = false
            },
            loadTabFields: function () {
                this.fields = (this.tabs[this.selectedTab] || []).map((f) => ({
                    field_key: f.field_key,
                    display_name: f.display_name === null ? '' : f.display_name,
                    is_visible: f.is_visible,
                }))
            },
            addField: function () {
                if (!this.newFieldKey) {
                    return
                }
                this.fields.push({
                    field_key: this.newFieldKey,
                    display_name: '',
                    is_visible: true,
                })
                this.newFieldKey = null
            },
            removeField: function (fieldKey) {
                // Remove the field with the given fieldKey from the fields array
                this.fields = this.fields.filter((f) => f.field_key !== fieldKey)
                console.log('Removed field:', fieldKey, 'Remaining fields:', this.fields)
                // Mark the removed field as not visible so it can be added back to the available catalog
                if (!this.availableCatalog.includes(fieldKey)) {
                    this.availableCatalog.push(fieldKey)
                }

            },
            toggleAllVisible: function (value) {
                this.fields.forEach((field) => {
                    field.is_visible = Boolean(value)
                })
            },
            moveField: function (fromIndex, toIndex) {
                if (toIndex < 0 || toIndex >= this.fields.length) {
                    return
                }
                const element = this.fields.splice(fromIndex, 1)[0]
                this.fields.splice(toIndex, 0, element)
            },
            discardChanges: function () {
                this.loadProfileFields()
            },
            save: async function () {
                this.isSaving = true
                const payload = this.fields.map((field, index) => ({
                    field_key: field.field_key,
                    display_name: field.display_name === '' ? null : field.display_name,
                    position: index,
                    is_visible: field.is_visible,
                }))

                await DebtorFieldService.saveProfileFields(this.selectedProfileId, this.selectedTab, payload).then((responseData) => {
                    if (!responseData.error) {
                        const store = useDebtorFieldsStore()
                        store.limpiar()
                        this.showSnackbar('Configuración guardada correctamente.')
                        this.loadProfileFields()
                    } else {
                        this.showSnackbar(responseData.msg)
                    }
                })
                this.isSaving = false
            },
            showSnackbar: function (description) {
                this.snackbarData.description = description
                this.snackbarData.openSnackbar = true
            },
        },
        watch: {
            selectedTab() {
                this.loadTabFields()
            },
        },
    }
</script>

<style scoped>
    .content_card {
        overflow: auto;
    }

    .content_main {
        padding: 20px;
        overflow: auto;
    }

    .toolbar {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 16px;
        flex-wrap: wrap;
        margin-bottom: 16px;
    }

    .profile_select {
        max-width: 360px;
        min-width: 260px;
    }

    .create_profile {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        flex: 1;
        min-width: 320px;
    }

    .tenant_select {
        max-width: 420px;
        min-width: 220px;
    }

    .tabs_bar {
        display: flex;
        flex-direction: row;
        gap: 8px;
        flex-wrap: wrap;
        margin-bottom: 16px;
    }

    .tab_chip {
        cursor: pointer;
    }

    .select_all_bar {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 4px;
        padding: 4px;
    }

    .select_all_hint {
        font-size: 11pt;
        color: #666666;
    }

    .columns_header,
    .column_row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
    }

    .columns_header {
        font-weight: 600;
        padding: 8px 4px;
        border-bottom: 1px solid #CDCDCD;
    }

    .column_row {
        padding: 4px;
        border-bottom: 1px solid #EFEFEF;
    }

    .col_position {
        width: 40px;
        text-align: center;
        flex-shrink: 0;
    }

    .col_key {
        width: 220px;
        flex-shrink: 0;
        word-break: break-word;
    }

    .col_name {
        flex: 1;
        min-width: 200px;
    }

    .col_visible {
        width: 110px;
        display: flex;
        justify-content: center;
        flex-shrink: 0;
    }

    .col_actions {
        width: 140px;
        display: flex;
        justify-content: flex-end;
        flex-shrink: 0;
    }

    .columns_list {
        max-height: 46vh;
        overflow: auto;
        border: 1px solid #E0E0E0;
        border-radius: 4px;
        padding: 4px 8px;
    }

    @media (max-width: 600px) {
        .columns_list {
            max-height: 30vh;
        }
    }

    @media (min-width: 601px) and (max-width: 1199px) {
        .columns_list {
            max-height: 36vh;
        }
    }

    @media (min-width: 1200px) and (max-height: 760px) {
        .columns_list {
            max-height: 38vh;
        }
    }

    .add_field {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        margin-top: 16px;
        max-width: 560px;
    }

    .catalog_select {
        flex: 1;
        max-width: 420px;
        min-width: 260px;
    }

    .empty_state {
        text-align: center;
        padding: 40px;
        color: #888888;
    }

    .actions_buttons {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 20px;
    }
</style>